import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import ListViewColumn from '../../../core/model/list-view-column';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { ListViewColumnFactory } from '../../../core/service/factory/list-view-column-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { vacantRentalObjectCommand } from '../../model/const/command';
import { RentalObjectCategory } from '../../model/const/enums';
import { VacantOtherRentalObject } from '../../model/vacant-other';
import { CreateContractCommandHandler } from '../../service/command/create-contract-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { VacantOtherService } from '../../service/data/vacant-other.service';
import { VacantOtherCommandHandlerResolver } from '../../service/command/vacant-other-command-handler-resolver.service';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { LanguageService } from '../../../core/service/language.service';
import { ActivatedRoute } from '@angular/router';
import { VacantListDrilldownState } from '../../helper/vacant-list-drilldown';

@Component({
    selector: 'app-vacant-period-other-manager',
    templateUrl: './vacant-period-other-manager.component.html',
    styleUrls: ['./vacant-period-other-manager.component.scss'],
    providers: [
        CreateContractCommandHandler,
        VacantOtherCommandHandlerResolver,
        { provide: 'category', useValue: RentalObjectCategory.Other },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VacantPeriodOtherManagerComponent extends MultiselectObjectManagerComponentBase<VacantOtherRentalObject> {
  excelColumnDefinition: ColumnDefinition<VacantOtherRentalObject>[] = [];
  excelContext = 'VacantOther';
  excelFileName = 'Lediga hyresobjekt övriga';
  selectedColumns: ListViewColumn<VacantOtherRentalObject>[] = [];
  selectedOtherIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<VacantOtherRentalObject>[] = [];
  private columnUpdateScheduled = false;
  private readonly legacyExcelColumnDefinitions: ColumnDefinition<VacantOtherRentalObject>[];
  private readonly vacantDrilldown = new VacantListDrilldownState();

  constructor(
    columnDefinitionService: ColumnDefinitionService,
    private _dataService: VacantOtherService,
    dispatcher: CommandDispatcher,
    handlerResolver: VacantOtherCommandHandlerResolver,
    messageService: MessageService,
    private readonly _languageService: LanguageService,
    navigationService: NavigationService,
    private _cdf: ColumnDefinitionFactory,
    private readonly _listViewColumnFactory: ListViewColumnFactory,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    activatedRoute: ActivatedRoute
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);
    this.AddSubscription(activatedRoute.queryParamMap.subscribe(params => this.vacantDrilldown.setQuery(params)));

    this.columnDefinition = columnDefinitionService.create('VacantRentalObject');
    this.legacyExcelColumnDefinitions = [
      this._cdf.createMoney('EXCEL_EXPORT_SETTINGS.CALENDAR_YEAR_RENT', 'calendarYearRent', ColumnVisibility.optional),
      this._cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'currentMonthRent', ColumnVisibility.optional),
      this._cdf.createMoney('EXCEL_EXPORT_SETTINGS.VACANT_MOVING_IN_RENT', 'movingInRent', ColumnVisibility.optional),
    ];
    this.excelColumnDefinition = [...this.columnDefinition, ...this.legacyExcelColumnDefinitions];
    this.selectedColumns = this._listViewColumnFactory.create(this.excelColumnDefinition).filter(column => column.selected);

    let tran = this._languageService.getCurrentTranslations();
    this.columnGroupDefinitions = columnDefinitionService.createWithTranslation('VacantRentalObjectGrouped', tran);

    this.title = 'VACANT_RENTAL_OBJECT_LIST.OTHER_RENTAL_OBJECT_HEADER';

    this.preferencesKey = 'rent.vacant-period.other.list';
    this.groupedPreferencesKey = 'rent.vacant-period.other.grouped';

    this.addContextMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', vacantRentalObjectCommand.createContract);

    this.menuDefinition = [
      new MenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_BLOCK_LEASE_AGREEMENT_BTN', vacantRentalObjectCommand.createBlockContract),
    ];
  }

  columnGroupDefinitions: ColumnDefinition[];
  groupedPreferencesKey: string;

  ngOnInit() {
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        definition = this.vacantDrilldown.applyToDefinition(definition);
        this.defaultFilters = definition.defaultFields;

        const rentalObjectTypes = definition.rentalObjectTypes.map(x => {
          return { caption: x.name, value: x.id };
        });
        rentalObjectTypes.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
        const yesNo = [
          { caption: 'YES_TEXT', value: 1 },
          { caption: 'NO_TEXT', value: 2 },
        ];
        yesNo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });
        const displaySelection = [
          { caption: 'COMMON.LIST', value: 1 },
          { caption: 'REPORT.GROUP_LEVEL_PROPERTY', value: 2 },
        ];
        displaySelection.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });
        const vacantPeriodSelection = [
          { caption: 'VACANT_RENTAL_OBJECT_LIST.ONLY_VACANT_UNTIL_FURTHER_NOTICE', value: 0 },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LIMITED_IN_TIME', value: 1 },
          { caption: 'ALL_TEXT', value: 2 },
        ];

        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company'),
            true
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
            'property',
            definition.property,
            this._propertyService,
            definition.selectedFields.includes('property'),
            true
          ),
          new FilterDefinitionItem(
            'COMMON.RENTAL_OBJECT_TYPE',
            'rentalObjectType',
            definition.rentalObjectType,
            rentalObjectTypes,
            definition.selectedFields.includes('rentalObjectType')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.STREET_COLUMN',
            'street',
            definition.street,
            null,
            definition.selectedFields.includes('street')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.CITY_COLUMN',
            'city',
            definition.city,
            null,
            definition.selectedFields.includes('city')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN',
            'rentalId',
            definition.rentalId,
            null,
            definition.selectedFields.includes('rentalId')
          ),
          new FilterDefinitionItem(
            'VACANT_RENTAL_OBJECT_LIST.DISPLAY_VACANT_PERIODS',
            'vacantPeriod',
            definition.vacantPeriod,
            vacantPeriodSelection,
            definition.selectedFields.includes('vacantPeriod')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT.ACTIVE_BLOCKING',
            'blockedRentalObjects',
            definition.blockedRentalObjects,
            yesNo,
            definition.selectedFields.includes('blockedRentalObjects')
          ),
          new FilterDefinitionItem(
            'COMMON.DISPLAY',
            'displayType',
            definition.displayType,
            displaySelection,
            definition.selectedFields.includes('displayType')
          ),
          new FilterDefinitionItemLocalDate(
            'VACANT_RENTAL_OBJECT_LIST.VACANT_ON_DATE',
            'vacantOnDate',
            definition.vacantOnDate,
            definition.selectedFields.includes('vacantOnDate')
          ),
        ];
      })
    );
  }

  dataExpandedList: ExpandedListViewItemDefinition[];

  private overviewSubscription: Subscription;

  currentDisplayType: number;

  get displayGroupedByProperty(): boolean {
    return this.currentDisplayType && this.currentDisplayType === 2;
  }

  get displayList(): boolean {
    return this.itemsSource && (this.currentDisplayType === undefined || this.currentDisplayType === 0 || this.currentDisplayType === 1);
  }

  get excelReady(): boolean {
    return !!this.filterState && this.selectedColumns.length > 0;
  }

  get selectedOtherCount(): number {
    return this.selectedOtherIds.length;
  }

  get allOthersSelected(): boolean {
    return this.selectedOtherCount > 0 && this.selectedOtherCount === this.itemsSource?.length;
  }

  get totalOthers(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<VacantOtherRentalObject>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedOthersChange(others: VacantOtherRentalObject[]): void {
    this.selectedOtherIds = this.displayGroupedByProperty ? [] : others.map(other => other.id);
  }

  onFilterChange(filterState: any) {
    if (!filterState.selectedFields || this.filterDefinition === undefined) return;
    filterState = this.vacantDrilldown.prepareFilter(filterState);

    this.filterState = filterState;
    this.selectedOtherIds = [];
    this.currentDisplayType = filterState.displayType;

    if (filterState.displayType === undefined || filterState.displayType === 0 || filterState.displayType === 1) {
      this.dataExpandedList = undefined;
      return super.onFilterChange(filterState);
    }

    if (this.overviewSubscription) {
      this.overviewSubscription.unsubscribe();
    }

    if (filterState.displayType === 2) {
      this.itemsSource = undefined;
      this.overviewSubscription = this._dataService.getVacancyOverview(filterState).subscribe(data => {
        this.dataExpandedList = data.map(overview => {
          const model = new ExpandedListViewItemDefinition();
          model.caption = overview.propertyNumber + ' ' + overview.propertyName + ' ' + overview.propertyMunicipality;
          model.columnDefinitions = [
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
            this._cdf.createNumber('VACANT_RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', {
              visibility: ColumnVisibility.optional,
              totalSum: true,
            }),
            this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
            this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
            this._cdf.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
            this._cdf.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
            this._cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
            this._cdf.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
            this._cdf.createMoney('COMMON.YEAR_RENT', 'yearRent'),
          ];
          model.dataContext = overview;
          model.icon = 'account-statement';
          model.itemsSource = overview.vacantRentalObjects.map(x => {
            return {
              rentalObjectId: x.rentalObjectId,
              rentalId: x.rentalId,
              address: x.address,
              rentalObjectType: x.rentalObjectType,
              surface: x.surface,
              vacantFrom: x.vacantFrom,
              vacantTo: x.vacantTo,
              blockedFrom: x.blockedFrom,
              blockedTo: x.blockedTo,
              blockingReason: x.blockingReason,
              monthlyRent: x.monthlyRent,
              yearRent: x.yearRent,
            };
          });
          return model;
        });
      });
    } else {
      this.dataExpandedList = undefined;
    }
  }

  protected clearAdditionalSubscriptions() {
    super.clearAdditionalSubscriptions();
    if (this.overviewSubscription) this.overviewSubscription.unsubscribe();
  }

  onRowClicked(e: { context: any; event: any }): void {
    const page = this.navigation.getParameterValue('page');
    this.navigation.navigate(['vacantother-rental-object', 'edit', e.context.rentalObjectId],
      page && /^\d+$/.test(page) ? { queryParams: { listPage: page } } : undefined, e.event.ctrlKey);
  }
}
